/**
 * ZDeploymentFolders - Zod Validation Schemas for Deployment Folders API
 *
 * Endpoints: /api/deployment-folders/*
 * Controller: deployments/infrastructure/controllers/deployment-folders.controller.ts
 *
 * UpdateIcon is multipart/form-data (Body typed as FormData for the generated
 * client; the backend reads the file via FileInterceptor).
 */

import { z } from "zod";

import {
  CreateFolderBodySchema,
  FolderIconRemovedSchema,
  FolderIconUpdatedSchema,
  FolderMutationOkSchema,
  FolderPathVarsSchema,
  FolderSchema,
  UpdateFolderBodySchema
} from "../../common/folder.schema";
import { ApiSuccessSchema } from "../../common/response.schema";

// ═══════════════════════════════════════════════════════════════
// SHARED SCHEMAS
// ═══════════════════════════════════════════════════════════════

const DeploymentFolderAssignmentSchema = z.object({
  deploymentId: z.string(),
  folderId: z.string()
});

export type DeploymentFolderAssignment = z.infer<typeof DeploymentFolderAssignmentSchema>;

const DeploymentFoldersGetAllResponseSchema = z.object({
  folders: z.array(FolderSchema),
  assignments: z.array(DeploymentFolderAssignmentSchema)
});

const AssignDeploymentBodySchema = z.object({
  // Deployment.id is a uuid column; the assignment table storing it is a bare
  // String with no foreign key, so an unvalidated id persists pointing at
  // nothing. The folderId sibling DOES have a foreign key, so a bad value there
  // surfaces as a Prisma error — a 500 where a 400 belongs. Only the body is
  // tightened — the response schema still describes rows written before this
  // check existed.
  deploymentId: z.string().uuid(),
  folderId: z.string().uuid().nullable()
});

// ═══════════════════════════════════════════════════════════════
// ZDEPLOYMENT FOLDERS MANIFEST
// ═══════════════════════════════════════════════════════════════

export const ZDeploymentFolders = {
  /** GET /api/deployment-folders — all folders + deployment assignments. */
  GetAll: {
    method: "GET" as const,
    path: "/api/deployment-folders",
    Response: ApiSuccessSchema(DeploymentFoldersGetAllResponseSchema)
  },

  /** POST /api/deployment-folders — create a folder. */
  Create: {
    method: "POST" as const,
    path: "/api/deployment-folders",
    Body: CreateFolderBodySchema,
    Response: ApiSuccessSchema(FolderSchema)
  },

  /** PATCH /api/deployment-folders/:id — rename or move a folder. */
  Update: {
    method: "PATCH" as const,
    path: "/api/deployment-folders/:id",
    PathVars: FolderPathVarsSchema,
    Body: UpdateFolderBodySchema,
    Response: ApiSuccessSchema(FolderSchema)
  },

  /** POST /api/deployment-folders/:id/updateIcon — multipart icon upload ("file" field). */
  UpdateIcon: {
    method: "POST" as const,
    path: "/api/deployment-folders/:id/updateIcon",
    PathVars: FolderPathVarsSchema,
    Body: z.custom<FormData>(),
    Response: ApiSuccessSchema(FolderIconUpdatedSchema)
  },

  /** POST /api/deployment-folders/:id/removeIcon — remove the folder icon. */
  RemoveIcon: {
    method: "POST" as const,
    path: "/api/deployment-folders/:id/removeIcon",
    PathVars: FolderPathVarsSchema,
    Response: ApiSuccessSchema(FolderIconRemovedSchema)
  },

  /** DELETE /api/deployment-folders/:id — delete a folder. */
  Delete: {
    method: "DELETE" as const,
    path: "/api/deployment-folders/:id",
    PathVars: FolderPathVarsSchema,
    Response: ApiSuccessSchema(FolderMutationOkSchema)
  },

  /** POST /api/deployment-folders/assign-item — assign a deployment (folderId null = root). */
  AssignItem: {
    method: "POST" as const,
    path: "/api/deployment-folders/assign-item",
    Body: AssignDeploymentBodySchema,
    Response: ApiSuccessSchema(FolderMutationOkSchema)
  }
} as const;
